"use client";

import React, { useCallback, useState } from "react";
import { Flex, message } from "antd";
import Button from "antd/es/button";
import Checkbox from "antd/es/checkbox";
import type { CheckboxChangeEvent } from "antd/es/checkbox";
import TextArea from "antd/es/input/TextArea";
import Text from "antd/es/typography/Text";
import Paragraph from "antd/es/typography/Paragraph";
import { getApiClient } from "../../../apiClient";

export function AppointmentNote({
  appointmentId,
  customerId,
}: {
  appointmentId: string;
  customerId?: string | null;
}) {
  const [isSubmitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showNote, setShowNote] = useState(false);
  const [note, setNote] = useState("");
  const [submittedNote, setSubmittedNote] = useState<string | null>(null);
  const [messageApi, contextHolder] = message.useMessage();

  const trimmedNote = note.trim();

  const handleToggle = useCallback((event: CheckboxChangeEvent) => {
    const nextChecked = event.target.checked;
    setShowNote(nextChecked);
    if (!nextChecked) {
      setNote("");
      setSubmitted(false);
      setSubmittedNote(null);
    }
  }, []);

  const onRequestSubmit = useCallback(async () => {
    if (!appointmentId) return;
    setSubmitting(true);
    try {
      const api = getApiClient();
      const res = customerId
        ? await api.appointments.addNotePublic({
            appointmentId,
            note: trimmedNote,
            customerId,
          })
        : await api.appointments.addNote({
            appointmentId,
            note: trimmedNote,
          });
      if (res.data.appointmentId) {
        setSubmitted(true);
        setSubmittedNote(trimmedNote);
        setShowNote(false);
        messageApi.success("Your note has been sent to your stylist.");
      }
    } catch {
      messageApi.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }, [appointmentId, customerId, messageApi, trimmedNote]);

  return (
    <div>
      {contextHolder}
      <Flex vertical gap="small" style={{ width: "100%" }}>
        <Text>
          Is there anything you would like to inform to your appointment?
        </Text>

        {submitted && submittedNote ? (
          <Flex vertical gap="small" style={{ width: "100%" }}>
            <Paragraph>Noted!</Paragraph>
          </Flex>
        ) : null}

        {!submitted && showNote ? (
          <Flex vertical gap="small" style={{ width: "100%" }}>
            <TextArea
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder="Share anything we should know ahead of time"
              autoSize={{ minRows: 3, maxRows: 6 }}
              maxLength={500}
            />
            <Button
              disabled={!trimmedNote}
              loading={isSubmitting}
              onClick={onRequestSubmit}
              type="dashed"
              style={{ alignSelf: "flex-start" }}
            >
              Save
            </Button>
          </Flex>
        ) : null}

        {!submitted && !showNote ? (
          <Checkbox checked={showNote} onChange={handleToggle}>
            Yes
          </Checkbox>
        ) : null}
      </Flex>
    </div>
  );
}
